/**
 * volto-vilax-next — the Vilax converged frontend addon (Volto 19.1.1 / VLT-8).
 *
 * The VLT-8 successor to volto-vilax, built for the vilaxnext cutover target
 * (DR-2026-203). Promotes to volto-vilax at the live flip. Differences vs the
 * VLT-7 volto-vilax:
 *   - Mobile nav is OWNED (VilaxMobileNav / .vmn-*) — the #1 shadow-paydown;
 *     VLT's MobileNavigation + the deleted MobileToolsFooter + the pastanaga-
 *     hamburger SCSS war are gone (see VilaxMobileNav.jsx + vilax-mobile.css).
 *   - The 7 dormant custom-type Views + the Möbelfakta dashboard are DROPPED
 *     (DR-2026-203 backend decision #3: the 7 custom types are not migrated —
 *     zero content — so vilax bootstraps as a standard Model-A tenant).
 *   - DocumentView (Page/Languageroot title-block suppression, DR-2026-142) and
 *     the ImageGallery block (DR-2026-087, used by 35 docs) are kept.
 */
import VilaxFooter from './components/Footer/VilaxFooter';
import VilaxHeader from './components/Header/VilaxHeader';
import DocumentView from './components/Views/DocumentView';
import ImageGalleryEdit from './components/Blocks/ImageGallery/Edit';
import ImageGalleryView from './components/Blocks/ImageGallery/View';
import './vilax-layout.css';
import './vilax-mobile.css';

const applyConfig = (config) => {
  // -- Widget overrides for Nick behaviors that lack widget definitions --
  // Nick's categorization behavior defines relatedItems as type:array with no
  // widget, which crashes Volto's widget factory (.toLowerCase() on undefined).
  // Map these fields to appropriate Volto widgets.
  config.widgets.id = {
    ...config.widgets.id,
    relatedItems: config.widgets.widget.object_browser,
    subjects: config.widgets.widget.tags || config.widgets.id?.subjects,
  };

  // -- Multilingual setup --
  config.settings.isMultilingual = true;
  config.settings.supportedLanguages = [
    'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'et', 'eu',
    'fi', 'fr', 'ga', 'hr', 'hu', 'it', 'ja', 'ko', 'lt', 'lv',
    'mt', 'nb', 'nl', 'pl', 'pt', 'pt-BR', 'ro', 'sk', 'sl', 'sv', 'zh-CN',
  ];
  config.settings.defaultLanguage = 'sv';

  // -- Content type views --
  // DR-2026-142: per-page title-block suppression. DocumentView wraps
  // DefaultView, filtering @type:title from blocks at view time when
  // content.suppress_title === true. Keys must match nick's actual @type
  // values (Page core 1002, Languageroot multilingual 1001, both via patches/).
  // The 7 Möbelfakta custom-type views (MobelfaktaProduct/Supplier/TestProtocol/
  // MaterialDeclaration/Certificate/AuditReport/Deviation) are intentionally
  // NOT registered here — those types are not migrated to Model-A vilax.
  config.views.contentTypesViews = {
    ...config.views.contentTypesViews,
    Page: DocumentView,
    Languageroot: DocumentView,
  };

  // -- ImageGallery block: side-by-side images with captions + lightbox --
  // DR-2026-087. Used by 35 vilax docs (the-physics-of-the-embrace, standard-beds,
  // rest_refined, om-vilax, ...). Depends on yet-another-react-lightbox.
  config.blocks.blocksConfig.imageGallery = {
    id: 'imageGallery',
    title: 'Image Gallery',
    icon: config.blocks.blocksConfig.image?.icon,
    group: 'media',
    view: ImageGalleryView,
    edit: ImageGalleryEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  // -- Vilax branded footer (registered in VLT "footer" slot) --
  // VLT's Footer component is shadowed (customizations/@kitconcept/...)
  // to render only the footer slot, removing preFooter/postFooter entirely.
  config.registerSlotComponent({
    slot: 'footer',
    name: 'vilaxFooter',
    component: VilaxFooter,
  });

  // -- Vilax branded header (registered in VLT "aboveHeader" slot) --
  // Per WI-SW-003 + BUG-2026-116: VilaxHeader mounts in aboveHeader and VLT's
  // stock Header is shadowed to render only the slots (no header-wrapper), so
  // VilaxHeader is the sole header — no DOM-duplicate, no second hamburger.
  config.registerSlotComponent({
    slot: 'aboveHeader',
    name: 'vilaxHeader',
    component: VilaxHeader,
  });

  // -- User menu: Change Password link --
  // Relocated to the shared volto-nick-compat addon (DR-2026-210) so the whole
  // converged fleet gets it; vilaxnext loads nick-compat, so it still appears
  // here. Removed from this addon to avoid a double <Plug id="change-password">.

  return config;
};

export default applyConfig;
